import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Award, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { whenFree } from "@/lib/popupQueue";
import { confetti, playFanfare } from "@/lib/celebrate";
import { useHonorTitle, useHonorTier, TIERS } from "@/lib/honor";

const COLORS = ["#ffffff", "#fff6d9", "#f3d27a", "#e2bb5c", "#c8962e", "#fffaf0"];

export function HonorWelcome() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const pending = Boolean(user?.honor_celebrate);
  const title = useHonorTitle(user?.user_id) || "Ehrenmitglied der itzPlay Community";
  const tier = useHonorTier(user?.user_id) || "gold";
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!pending) return;
    whenFree("honor-welcome", () => {
      setOpen(true);
      playFanfare();
      [0, 400, 800, 1300].forEach((d, i) => setTimeout(() => confetti({ x: innerWidth * [0.5, 0.2, 0.8, 0.5][i], y: innerHeight * 0.3, count: 80, spread: 1.7, colors: COLORS }), d));
    });
  }, [pending]);
  if (!pending || !open) return null;
  const close = (to) => {
    setOpen(false);
    setUser({ ...user, honor_celebrate: undefined });
    api.post("/honor/celebrated").catch(() => {});
    if (to) nav(to);
  };
  return (
    <div role="dialog" aria-label="Ehrenmitglied-Willkommen" className="fixed inset-0 z-[80] flex items-center justify-center p-4 honor-welcome-bg backdrop-blur-md fade-up" data-testid="honor-welcome">
      <div className={`tier-${tier} relative w-full max-w-md text-center space-y-5 honor-card rounded-[2rem] px-6 py-8`}>
        <div className="relative mx-auto w-24 h-24 vip-crown-pop">
          <span className="absolute inset-0 rounded-full bg-amber-200/60 blur-2xl animate-pulse" />
          <span className="relative w-24 h-24 rounded-full honor-ring flex items-center justify-center"><span className="w-[84px] h-[84px] rounded-full bg-white flex items-center justify-center"><Award className="w-12 h-12 text-[#c8962e]" strokeWidth={2.2} /></span></span>
          <Sparkles className="absolute -top-2 -right-3 w-7 h-7 text-amber-400 vip-twinkle" />
          <Sparkles className="absolute bottom-0 -left-4 w-5 h-5 text-amber-300 vip-twinkle" style={{ animationDelay: "0.6s" }} />
        </div>
        <div>
          <p className="text-[11px] font-extrabold tracking-[0.35em] honor-title">EINE BESONDERE EHRE · {TIERS[tier].label.toUpperCase()}</p>
          <h2 className="font-display text-3xl font-extrabold mt-2 honor-name" data-testid="honor-welcome-title">Herzlichen Glückwunsch, {user.name}!</h2>
          <p className="text-slate-600 text-sm mt-3 leading-relaxed" data-testid="honor-welcome-text">Saskia & Stefan haben dich zum <b className="honor-title">{title}</b> ernannt. Ab sofort strahlt dein Profil in Weiß-Gold, dein Profilbild trägt einen goldenen Rahmen und neben deinem Namen steht überall „Ehrenmitglied“. Danke, dass es dich gibt! 🤍</p>
        </div>
        <div className="flex flex-col gap-2 justify-center">
          <button onClick={() => close(`/u/${user.user_id}`)} data-testid="honor-welcome-profile" className="h-12 px-6 whitespace-nowrap rounded-full honor-chip font-extrabold active:scale-95 transition-transform">Mein Ehren-Profil ansehen</button>
          <button onClick={() => close("/ehrenwand")} data-testid="honor-welcome-wall" className="h-12 px-6 rounded-full border border-amber-200 text-[#8a5d0e] font-bold hover:bg-amber-50 active:scale-95 transition-[transform,background-color]">Zur Ehrenwand</button>
        </div>
      </div>
    </div>
  );
}
